'use memo';

import { type ChangeEvent, type CompositionEvent, type InputHTMLAttributes, forwardRef, useCallback, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import Icon from './Icon/Icon';
import { createInputChangeEvent, resolveDisplayedValue, shouldSkipDuplicateChangeAfterCompositionEnd, shouldSuppressChangeDuringComposition } from './SearchInput.composition';

// IME composition 中は親へ onChange を流さない（確定時のみ）。
// controlled 時は親 value が追いつくまで draft を優先表示する（composition 中の書き戻し防止、
// および debounce 親で確定直後に古い value へ戻るのを防ぐ）。
// URL 同期・debounce は呼び出し側の責務（value={searchParam} + 毎キー navigate は避ける）。

type Props = InputHTMLAttributes<HTMLInputElement>;
const SearchInput = forwardRef<HTMLInputElement, Props>(({
  className,
  type = 'text',
  onChange,
  onCompositionStart,
  onCompositionEnd,
  value,
  ...rest
}, ref) => {
  const isComposingRef = useRef(false);
  const lastEmittedOnEndRef = useRef<string | null>(null);
  const [draft, setDraft] = useState<string | null>(null);
  const [prevValue, setPrevValue] = useState(value);
  const isControlled = value !== undefined;

  // 親 value の変更に追従して draft をクリア（composition 中は維持）。
  // debounce 後の追いつき・外部クリアの両方をカバーする。
  // @see https://react.dev/reference/react/useState#storing-information-from-previous-renders
  /* oxlint-disable react/refs -- composition 中の ref を render で読む */
  if (isControlled && value !== prevValue) {
    setPrevValue(value);
    if (!isComposingRef.current) {
      setDraft(null);
    }
  }
  /* oxlint-enable react/refs */

  const handleCompositionStart = useCallback((e: CompositionEvent<HTMLInputElement>) => {
    isComposingRef.current = true;
    lastEmittedOnEndRef.current = null;
    onCompositionStart?.(e);
    if (isControlled) {
      setDraft(e.currentTarget.value);
    }
  }, [isControlled, onCompositionStart]);
  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (shouldSkipDuplicateChangeAfterCompositionEnd({
      lastEmittedValue: lastEmittedOnEndRef.current,
      nextValue: e.target.value
    })) {
      lastEmittedOnEndRef.current = null;
      return;
    }
    const nativeEventIsComposing = e.nativeEvent instanceof InputEvent ? e.nativeEvent.isComposing : false;
    const suppress = shouldSuppressChangeDuringComposition({
      isComposingRef: isComposingRef.current,
      nativeEventIsComposing
    });
    if (suppress) {
      if (isControlled) {
        setDraft(e.target.value);
      }
      return;
    }
    lastEmittedOnEndRef.current = null;
    if (isControlled) {
      setDraft(e.target.value);
    }
    onChange?.(e);
  }, [isControlled, onChange]);
  const handleCompositionEnd = useCallback((e: CompositionEvent<HTMLInputElement>) => {
    isComposingRef.current = false;
    onCompositionEnd?.(e);
    const input = e.currentTarget;
    const confirmedValue = input.value;
    lastEmittedOnEndRef.current = confirmedValue;

    // debounce 親では props value が遅れるため、追いつくまで draft を残す。
    if (isControlled) {
      setDraft(confirmedValue);
    }
    onChange?.(createInputChangeEvent(input));
  }, [isControlled, onChange, onCompositionEnd]);
  const displayedValue = resolveDisplayedValue({
    isControlled,
    draft,
    propsValue: value
  });
  return <div className={cn('flex h-9 w-full items-center rounded-md border border-neutral-muted-border bg-neutral-fill px-3 shadow-xs', 'has-[input:focus]:ring-1 has-[input:focus]:ring-ring', className)}>
        <Icon iconName="SearchIcon" className="mr-1.5 text-muted-foreground" />
        <input {...rest} ref={ref} type={type} className="w-full text-xs outline-hidden placeholder:text-muted-foreground" {...isControlled ? {
      value: displayedValue
    } : {}} onChange={handleChange} onCompositionStart={handleCompositionStart} onCompositionEnd={handleCompositionEnd} />
      </div>;
});
SearchInput.displayName = 'SearchInput';
export default SearchInput;